import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { EmptyRecordsState } from '../../common/ResponsiveRecordList';
import { useLedgerLookups } from '../Journals';
import { CustomerPicker, FiscalPeriodPicker, useLedgerOptions } from '../LedgerPickers';
import { StatStrip, getErrorMessage, unwrap } from './ArShared';

const today = () => new Date().toISOString().slice(0, 10);

// Verified against accounting-apis (subsidiary-ledger + customer-invoice
// modules). The ledger strips/forbids unknown query keys, so each view sends
// ONLY its documented params. Row/field reads keep fallback keys where the
// shape is defensive, but the core contracts below are live-verified.

// fetchAging defaults to the 'receivables' gateway section; FeesBilling.js
// passes ledgerAPI.getFeeAgingReport (same shape, 'fees' section) so it can
// reuse this exact view under RESOURCES.LEDGER.FEES.
//
// Live contract: accounts-receivable/invoices/debtor-aging takes ONLY
// {currency_id?, customer_id?} - always as of now, no as_of_date.
// (ap-ar-reports/aging/debtors exists but is a stub returning empty rows.)
export const AgingReportView = ({ fetchAging = ledgerAPI.getArAgingReport }) => {
  const look = useLedgerLookups();
  const [customerId, setCustomerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = customerId ? { customer_id: customerId } : {};
      const res = await fetchAging(params);
      setPayload(unwrap(res));
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch aging report'));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = Array.isArray(payload)
    ? payload
    : (payload?.rows ?? payload?.items ?? payload?.customers ?? []);
  const totals = payload?.totals ?? {};

  const rowLabel = (r) => r.customer_name ?? r.name ?? r.customer?.name ?? look.customerLabel(r.customer_id, `Customer ${r.customer_id ?? ''}`);
  // debtor-aging rows carry bucketed invoices (age_bucket + balance_due)
  // rather than pre-bucketed row totals - aggregate them per bucket.
  const bucket = (r, key) => {
    if (r[key] !== undefined && r[key] !== null) return Number(r[key]);
    if (Array.isArray(r.invoices)) {
      return r.invoices.reduce((s, inv) => s + (inv.age_bucket === key ? Number(inv.balance_due) || 0 : 0), 0);
    }
    return 0;
  };

  const handleExport = () => {
    const headers = ['Student', 'Current', '1-30', '31-60', '61-90', '90+', 'Total'];
    const csvRows = rows.map((r) => [
      rowLabel(r), bucket(r, 'current'), bucket(r, 'days_1_30'), bucket(r, 'days_31_60'), bucket(r, 'days_61_90'), bucket(r, 'over_90'),
      r.total ?? (bucket(r, 'current') + bucket(r, 'days_1_30') + bucket(r, 'days_31_60') + bucket(r, 'days_61_90') + bucket(r, 'over_90')),
    ]);
    downloadCsv(`ar-aging-${today()}.csv`, headers, csvRows);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 240 }}>
          <CustomerPicker value={customerId} onChange={(id) => setCustomerId(id || '')} />
        </Box>
        <Button size="small" variant="outlined" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Generate'}</Button>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={!rows.length}>Export CSV</Button>
        <Typography variant="caption" color="text.secondary">Live balances, as of now.</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {totals && Object.keys(totals).length > 0 && (
        <StatStrip
          tiles={[
            { label: 'Current', value: formatAccountingAmount(totals.current) },
            { label: '1-30 days', value: formatAccountingAmount(totals.days_1_30), color: 'warning.main' },
            { label: '31-60 days', value: formatAccountingAmount(totals.days_31_60), color: 'warning.main' },
            { label: '61-90 days', value: formatAccountingAmount(totals.days_61_90), color: 'error.main' },
            { label: '90+ days', value: formatAccountingAmount(totals.over_90), color: 'error.main' },
            { label: 'Total', value: formatAccountingAmount(totals.total), color: 'primary.main' },
          ]}
        />
      )}

      {!loading && rows.length === 0 ? (
        <EmptyRecordsState message="No outstanding balances as of this date." />
      ) : (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Current</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">1-30</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">31-60</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">61-90</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">90+</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.customer_id ?? i} hover>
                  <TableCell>{rowLabel(r)}</TableCell>
                  <TableCell align="right">{formatAccountingAmount(bucket(r, 'current'), { blankZero: true })}</TableCell>
                  <TableCell align="right">{formatAccountingAmount(bucket(r, 'days_1_30'), { blankZero: true })}</TableCell>
                  <TableCell align="right">{formatAccountingAmount(bucket(r, 'days_31_60'), { blankZero: true })}</TableCell>
                  <TableCell align="right">{formatAccountingAmount(bucket(r, 'days_61_90'), { blankZero: true })}</TableCell>
                  <TableCell align="right">{formatAccountingAmount(bucket(r, 'over_90'), { blankZero: true })}</TableCell>
                  <TableCell align="right"><strong>{formatAccountingAmount(r.total ?? (bucket(r, 'current') + bucket(r, 'days_1_30') + bucket(r, 'days_31_60') + bucket(r, 'days_61_90') + bucket(r, 'over_90')))}</strong></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// Live contract: ap-ar-reports/payment-forecast takes ONLY {days_ahead?}
// (1-365, default 30). NOTE the ledger's feed is currently a stub returning
// [] - the view is wired correctly and will populate once the feed lands.
const PaymentForecastView = () => {
  const [daysAhead, setDaysAhead] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const n = Math.min(365, Math.max(1, Number(daysAhead) || 30));
      const res = await ledgerAPI.getArPaymentForecast({ days_ahead: n });
      setPayload(unwrap(res));
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch payment forecast'));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = Array.isArray(payload)
    ? payload
    : (payload?.rows ?? payload?.periods ?? payload?.items ?? []);
  const rowAmount = (r) => Number(r.expected_amount ?? r.amount ?? r.total ?? 0);
  const total = payload?.total ?? rows.reduce((s, r) => s + rowAmount(r), 0);

  const handleExport = () => {
    const headers = ['Period', 'Expected Amount', 'Invoices'];
    const csvRows = rows.map((r) => [r.period_label ?? r.period ?? r.due_date ?? r.week ?? r.month ?? '-', rowAmount(r), r.invoice_count ?? r.count ?? '']);
    csvRows.push(['TOTAL', total, '']);
    downloadCsv(`ar-payment-forecast-${daysAhead}d-${today()}.csv`, headers, csvRows);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          size="small" type="number" label="Days ahead" value={daysAhead}
          onChange={(e) => setDaysAhead(e.target.value)}
          inputProps={{ min: 1, max: 365, step: 1 }} sx={{ width: 140 }}
        />
        <Button size="small" variant="outlined" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Generate'}</Button>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={!rows.length}>Export CSV</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && rows.length === 0 ? (
        <EmptyRecordsState message="No expected receipts in this date range." />
      ) : (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Expected Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Invoices</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell>{r.period_label ?? r.period ?? r.due_date ?? r.week ?? r.month ?? '-'}</TableCell>
                  <TableCell align="right">{formatAccountingAmount(rowAmount(r))}</TableCell>
                  <TableCell align="right">{r.invoice_count ?? r.count ?? '-'}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'primary.contrastText', fontWeight: 700 }}>TOTAL</TableCell>
                <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 700 }}>{formatAccountingAmount(total)}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// Live contract: ap-ar-reports/reconciliation/ar takes ONLY {period_id?}
// and returns {period_id, period_name, control_account_id,
// control_account_code, subledger_total, gl_control_balance, difference,
// is_reconciled, checked_at}. Defaults to the period containing today.
const ReconciliationView = () => {
  const [periodId, setPeriodId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  // Same 'lookup-periods' raw cache Journals.js shares - never the mapped
  // FiscalPeriodPicker options (different shape, same key-collision lesson).
  const { options: periodsRaw } = useLedgerOptions('lookup-periods', () =>
    ledgerAPI.getFiscalPeriods({ limit: 100 }).then((res) => {
      const p = res?.data?.data ?? res?.data ?? {};
      return Array.isArray(p) ? p : p.items ?? [];
    })
  );

  useEffect(() => {
    if (periodId || !periodsRaw.length) return;
    const t = new Date(`${today()}T00:00:00`).getTime();
    const match = periodsRaw.find((p) => {
      if (!p.start_date || !p.end_date) return false;
      return t >= new Date(p.start_date).getTime() && t <= new Date(p.end_date).getTime();
    }) || periodsRaw[0];
    if (match) setPeriodId(match.period_id ?? match.id);
  }, [periodsRaw, periodId]);

  const load = async () => {
    if (!periodId) {
      setError('Select a fiscal period first.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await ledgerAPI.getArReconciliationReport({ period_id: periodId });
      setPayload(unwrap(res));
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch reconciliation report'));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (periodId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId]);

  const subledgerTotal = Number(payload?.subledger_total ?? payload?.subledger_balance ?? 0);
  const glBalance = Number(payload?.gl_control_balance ?? payload?.gl_balance ?? 0);
  const difference = Number(payload?.difference ?? (subledgerTotal - glBalance));
  const items = payload?.unreconciled_items ?? payload?.items ?? payload?.lines ?? [];
  const isReconciled = payload && Math.abs(difference) < 0.005;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 260 }}>
          <FiscalPeriodPicker value={periodId} onChange={(id) => setPeriodId(id || '')} />
        </Box>
        <Button size="small" variant="outlined" onClick={load} disabled={loading || !periodId}>{loading ? 'Loading...' : 'Generate'}</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {payload && (
        <>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {payload.period_name || ''}{payload.control_account_code ? ` · Control ${payload.control_account_code}` : ''}{payload.checked_at ? ` · Checked ${new Date(payload.checked_at).toLocaleString()}` : ''}
          </Typography>
          <StatStrip
            tiles={[
              { label: 'AR Subledger Total', value: formatAccountingAmount(subledgerTotal) },
              { label: 'GL Control Account', value: formatAccountingAmount(glBalance) },
              { label: 'Difference', value: formatAccountingAmount(difference), color: isReconciled ? 'success.main' : 'error.main' },
            ]}
          />
          {isReconciled ? (
            <Alert severity="success">AR subledger ties out to the GL control account.</Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Subledger and GL control account differ by {formatAccountingAmount(Math.abs(difference))}.
            </Alert>
          )}
          {items.length > 0 && (
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto', mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{it.reference ?? it.document_number ?? '-'}</TableCell>
                      <TableCell>{it.description ?? '-'}</TableCell>
                      <TableCell align="right">{formatAccountingAmount(it.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

// Selected from the right-side "Reports" menu in StudentReceivables.js
// (same pattern as CoaAccounts.js's Configuration menu) rather than a plain
// tab of its own - these are report views, not a document list.
export const AR_REPORT_ITEMS = [
  { value: 'aging', label: 'Aging', Component: AgingReportView },
  { value: 'forecast', label: 'Payment Forecast', Component: PaymentForecastView },
  { value: 'reconciliation', label: 'Reconciliation', Component: ReconciliationView },
];

export const AR_REPORTS_DISCLAIMER = "Aging and reconciliation read the live ledger contracts (debtor-aging is always as of now). The payment-forecast feed is wired but the ledger returns no rows for it yet.";
